import React, { useEffect, useState } from "react";
import Sidebar from "../sidebar";
import { useMutation } from "@apollo/client";
import { LOGOUT_OPERATOR_ACCOUNT } from "../../../constants/graphql/auth-queries";
import GlobalButton from "../../core/global-button";
import GlobalDialog from "../../core/global-dialog";
import { Avatar } from "primereact/avatar";
import { OverlayPanel } from "primereact/overlaypanel";
import { useNavigate } from "react-router-dom";

const AppbarWithSidebar = ({
  element,
  sidebarTitle,
  appbarTitle,
  logo,
  items,
}) => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [isUsername, setIsUsername] = useState("");
  const [isEmail, setIsEmail] = useState("");
  const [adminAccess, setAdminAccess] = useState(false);
  const avatarMenuRef = React.useRef(null);

  const [logoutOperatorAccount] = useMutation(LOGOUT_OPERATOR_ACCOUNT);

  const handleLogout = async () => {
    try {
      await logoutOperatorAccount();
    } catch (e) {
      console.error("Logout mutation failed:", e);
    } finally {
      // Set before clear so other tabs catch the storage event
      localStorage.setItem("logoutTriggered", Date.now().toString());
      localStorage.clear();
      navigator.clipboard.writeText("").catch(() => {});
      window.location.replace("/operator/admin/");
    }
  };

  const loadUserData = () => {
    const userLS = localStorage.getItem("user");
    if (userLS) {
      try {
        const parsedData = JSON.parse(userLS);
        setIsUsername(parsedData.username || "");
        setIsEmail(parsedData.email || "");
        setAdminAccess(parsedData.admin_access || false);
      } catch (error) {
        console.error("Failed to parse user data", error);
      }
    }
  };

  useEffect(() => {
    loadUserData();
  }, []);

  const getInitials = (name) => {
    if (!name) return "";
    const names = name.split(" ");
    if (names.length === 1) return names[0].substring(0, 2).toUpperCase();
    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="overflow-y-hidden h-full">
      <div className="grid grid-cols-5">
        <Sidebar
          className={`h-screen small_phone:w-2/3 small_phone:z-50 small_phone:fixed lg:w-full lg:static lg:translate-x-0 col-span-1 transform transition-transform ease-in-out small_phone:duration-300 ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } basis-1/5`}
          title={sidebarTitle}
          items={items}
          setShow={setSidebarOpen}
        />
        <div className="overflow-y-auto small_phone:col-span-5 w-full lg:col-span-4">
          <header className="bg-secondary-darkGray text-primary-primaryYellow p-4">
            <div className="flex justify-between items-center">
              <button
                className="lg:hidden focus:outline-none focus:shadow-outline-blue"
                onClick={toggleSidebar}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M4 6h16M4 12h16m-7 6h7"></path>
                </svg>
              </button>
              <div className="flex items-center space-x-2">
                <div className="w-28 h-full cursor-pointer relative hidden md:hidden lg:block">
                  <img src={logo} alt="MBOX Logo" />
                </div>
                <h1 className="text-xl font-semibold">{appbarTitle}</h1>
              </div>
              <div className="items-center space-x-4 flex">
                <GlobalButton
                  isRounded={true}
                  buttonClassName="p-0 px-2 border-0 w-auto bg-slate-700/10 py-1"
                  style={{ boxShadow: "none", outline: "none" }}
                  onClick={(e) => avatarMenuRef.current.toggle(e)}
                >
                  <Avatar
                    label={getInitials(isUsername)}
                    style={{ color: "#374151", cursor: "pointer" }}
                    className="bg-primary-primaryBlue h-[36px] w-[36px]"
                    shape="circle"
                  />
                  <div className="flex-col items-start hidden md:flex mx-3">
                    <div className="text-sm font-bold text-primary-primaryYellow">
                      {isUsername}
                    </div>
                    <div className="text-xs text-gray-400">
                      {adminAccess ? "Super Admin" : isEmail}
                    </div>
                  </div>
                </GlobalButton>
              </div>
            </div>
          </header>
          <div className="p-2 small_phone:h-full lg:h-44">{element}</div>
        </div>
      </div>

      <OverlayPanel ref={avatarMenuRef} className="font-montserrat">
        <div className="flex flex-col gap-2 w-48">
          <button
            onClick={() => {
              avatarMenuRef.current.hide();
              navigate("/settings/landing");
            }}
            className="flex items-center gap-3 py-3 hover:bg-gray-100 transition-colors text-left"
          >
            <i className="pi pi-cog text-gray-600"></i>
            <span className="text-gray-800 font-medium">Settings</span>
          </button>
          <button
            onClick={() => {
              avatarMenuRef.current.hide();
              setLogoutModalOpen(true);
            }}
            className="flex items-center gap-3 py-3 hover:bg-gray-100 transition-colors text-left"
          >
            <i className="pi pi-sign-out text-gray-600"></i>
            <span className="text-gray-800 font-medium">Log out</span>
          </button>
        </div>
      </OverlayPanel>

      <GlobalDialog
        visible={logoutModalOpen}
        onHide={() => setLogoutModalOpen(false)}
        closable={false}
        dialogHeader={
          <div className="flex flex-col items-center justify-center">
            <img
              alt="logout-icon"
              className="h-[90px] mr-2"
              src={`${process.env.PUBLIC_URL}/assets/images/warning.png`}
            />
          </div>
        }
        dialogClassName="w-[500px]"
      >
        <div className="font-montserrat">
          <div className="text-lg text-center font-semibold mb-4">
            Are you sure you want to logout?
          </div>
          <div className="text-center text-gray-600 text-sm mb-6">
            You will be redirected to the login page.
          </div>
          <div className="flex gap-2 items-center justify-center">
            <GlobalButton
              label="Cancel"
              buttonClassName="bg-white text-black border border-primary-primaryYellow px-6 py-2 hover:bg-slate-100 font-normal"
              onClick={() => setLogoutModalOpen(false)}
            />
            <GlobalButton
              label="Logout"
              buttonClassName="bg-red-600 text-white border-0 hover:bg-red-700 px-6 py-2"
              onClick={handleLogout}
            />
          </div>
        </div>
      </GlobalDialog>
    </div>
  );
};

export default AppbarWithSidebar;
